// client/src/components/templates/contratos/TemplateContratoForm.js - VERSÃO CORRIGIDA
import React, { useState, useEffect, useRef } from 'react';
import { useMutateTemplateContrato, useContratoPlaceholders } from '../../../hooks/useTemplateContrato';
import { useTiposContrato } from '../../../hooks/useTiposContrato';
import useAlert from '../../../hooks/useAlert';
import { useAuth } from '../../../hooks/useAuth';
import { isMatrizAdmin as checkIsMatrizAdmin } from '../../../utils/permissions';
import { Save, XCircle, Loader2, Code, Eye, ClipboardCopy, ChevronDown } from 'lucide-react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import PlaceholdersPanel from '../PlaceholdersPanel';
import GerarTextoIABox from '../../ai/GerarTextoIABox';

const initialState = {
    nome: '',
    descricao: '',
    conteudo: '<p>Contrato firmado com {{cliente.nome}}, no valor de {{contrato.valorTotalFormatado}}.</p>{{itensTable}}',
    tipoContrato: '',
    ativo: true,
    isPadrao: false,
};

const TemplateContratoForm = ({ onCloseForm, current }) => {
    const { user } = useAuth();
    const { setAlert } = useAlert();
    const { addTemplateContrato, updateTemplateContrato, isPendingContrato: isMutatingTemplate } = useMutateTemplateContrato();
    const { data: tiposContrato = [], isLoading: isLoadingTiposContrato } = useTiposContrato();
    const { data: placeholders, isLoading: isLoadingPlaceholders } = useContratoPlaceholders();

    const [template, setTemplate] = useState(initialState);
    const [viewMode, setViewMode] = useState('editor');
    const [showVariables, setShowVariables] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const editorRef = useRef(null);
    
    const isMatrizAdmin = checkIsMatrizAdmin(user) && !user?.account && !user?.franquia;

    // ✅ CORREÇÃO: useEffect corrigido para não resetar o formulário
    useEffect(() => { 
        // Só executa uma vez quando o componente monta ou quando current muda significativamente
        if (!isInitialized && current) {
            console.log('🔄 Inicializando formulário com dados do template:', current);
            setTemplate({
                _id: current._id,
                nome: current.nome || '',
                descricao: current.descricao || '',
                conteudo: current.conteudo || initialState.conteudo,
                tipoContrato: current.tipoContrato?._id || current.tipoContrato || '',
                ativo: current.ativo !== undefined ? current.ativo : true,
                isPadrao: current.isPadrao || false,
            });
            setIsInitialized(true);
        } else if (!current && !isInitialized) {
            // Novo template - usa initialState
            setTemplate(initialState);
            setIsInitialized(true);
        }
    }, [current, isInitialized]);

    // ✅ CORREÇÃO: Resetar o estado quando o componente for desmontado
    useEffect(() => {
        return () => {
            setIsInitialized(false);
        };
    }, []);

    const onChange = e => {
        const { name, value, type, checked } = e.target;
        setTemplate(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };
    
    const onEditorChange = (event, editor) => {
        setTemplate(prev => ({ ...prev, conteudo: editor.getData() }));
    };
    
    const onSubmit = async e => {
        e.preventDefault();
        
        // Validações
        if (!template.nome.trim()) {
            setAlert('O nome do template é obrigatório.', 'danger');
            return;
        }
        
        if (!template.tipoContrato) {
            setAlert('O tipo de contrato é obrigatório.', 'danger');
            return;
        }

        const payload = { 
            ...template,
            tipoContrato: template.tipoContrato || null
        };
        
        if (!isMatrizAdmin) {
            delete payload.isPadrao;
        }

        console.log('📤 Enviando payload:', payload);

        try {
            if (current) {
                await updateTemplateContrato.mutateAsync({ 
                    id: template._id, 
                    templateData: payload 
                });
            } else {
                await addTemplateContrato.mutateAsync(payload);
            }
            onCloseForm();
        } catch (error) {
            console.error("❌ Erro ao salvar template:", error);
            setAlert(error.response?.data?.error || 'Falha ao salvar o template.', 'danger');
        }
    };
    
    const handleClose = () => { 
        onCloseForm(); 
    };
    
    const insertPlaceholder = (text) => {
        const editor = editorRef.current;
        if (editor) {
            editor.model.change(writer => {
                const insertPosition = editor.model.document.selection.getFirstPosition();
                writer.insertText(text, insertPosition);
            });
            editor.editing.view.focus();
            setShowVariables(false); 
        } else {
            setAlert('Editor não está pronto. Tente novamente.', 'warning');
        }
    };

    const overallLoading = isLoadingTiposContrato || isMutatingTemplate;

    return (
        <form onSubmit={onSubmit} noValidate>
                    <div className="form-header">
                        <h3 className="form-title">
                            {current ? `Editar Template: ${template.nome}` : 'Novo Template de Contrato'}
                        </h3>
                        <button type="button" onClick={handleClose} title="Fechar" className="form-close-button">
                            <XCircle size={24} />
                        </button>
                    </div>
                    
                    <fieldset className="form-section">
                        <legend className="form-section-title">Informações Gerais</legend>
                        <div className="form-grid-container">
                            <div className="form-group">
                                <label htmlFor="nome">Nome do Template*</label>
                                <input 
                                    type="text" 
                                    id="nome" 
                                    name="nome" 
                                    value={template.nome} 
                                    onChange={onChange} 
                                    required 
                                    className="form-input"
                                    placeholder="Digite o nome do template"
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="tipoContrato">Tipo de Contrato Associado*</label>
                                <select 
                                    id="tipoContrato" 
                                    name="tipoContrato" 
                                    value={template.tipoContrato || ''} 
                                    onChange={onChange} 
                                    required 
                                    className="form-input" 
                                    disabled={overallLoading}
                                >
                                    <option value="">-- Selecione --</option>
                                    {isLoadingTiposContrato ? (
                                        <option disabled>Carregando tipos...</option>
                                    ) : (
                                        tiposContrato.map(t => (
                                            <option key={t._id} value={t._id}>
                                                {t.nome}
                                            </option>
                                        ))
                                    )}
                                </select>
                            </div>
                            <div className="form-group full-width">
                                <label htmlFor="descricao">Descrição</label>
                                <textarea 
                                    id="descricao" 
                                    name="descricao" 
                                    value={template.descricao || ''} 
                                    onChange={onChange} 
                                    rows="2" 
                                    className="form-input" 
                                    placeholder="Descrição opcional do template"
                                />
                            </div>
                        </div>
                    </fieldset>

                    <fieldset className="form-section">
                        <h4 className="form-section-title">Conteúdo do Template*</h4>
                        <GerarTextoIABox
                            endpoint="/templates/contrato/gerar-ia"
                            extraPayload={{ tipoContrato: template.tipoContrato || undefined }}
                            placeholder='Descreva o contrato — ex: "prestação de serviço de manutenção mensal, pagamento recorrente, multa por cancelamento antecipado"'
                            successMessage="Contrato gerado! Revise o texto antes de salvar."
                            onGenerated={(conteudoGerado) => {
                                setTemplate(prev => ({ ...prev, conteudo: conteudoGerado }));
                                if (editorRef.current) editorRef.current.setData(conteudoGerado);
                            }}
                        />
                        <div className="editor-toolbar">
                            <button 
                                type="button" 
                                onClick={() => setShowVariables(!showVariables)} 
                                className="action-button action-button-secondary"
                            >
                                <ClipboardCopy size={16} className="icon"/> Inserir Variável
                                <ChevronDown size={16} className={`transition-transform ${showVariables ? 'rotate-180' : ''}`}/>
                            </button>
                            <button 
                                type="button" 
                                onClick={() => setViewMode(viewMode === 'preview' ? 'editor' : 'preview')} 
                                className="action-button action-button-secondary"
                            >
                                <Eye size={16} className="icon"/> 
                                {viewMode === 'preview' ? 'Voltar ao Editor' : 'Preview'}
                            </button>
                            <button 
                                type="button" 
                                onClick={() => setViewMode(viewMode === 'source' ? 'editor' : 'source')} 
                                className="action-button action-button-secondary"
                            >
                                <Code size={16} className="icon"/> 
                                {viewMode === 'source' ? 'Editor Visual' : 'Código HTML'}
                            </button>
                        </div>
                        {showVariables && <PlaceholdersPanel placeholders={placeholders} isLoading={isLoadingPlaceholders} onInsert={insertPlaceholder} />}
                        <div className="editor-container">
                            {viewMode === 'editor' && (
                                <CKEditor 
                                    editor={ClassicEditor} 
                                    data={template.conteudo} 
                                    onChange={onEditorChange} 
                                    onReady={(editor) => {
                                        editorRef.current = editor;
                                        console.log('✅ Editor CKEditor carregado');
                                    }}
                                    key={current ? `editor-${current._id}` : 'editor-new'}
                                />
                            )}
                            {viewMode === 'preview' && (
                                <div 
                                    className="editor-preview prose max-w-none" 
                                    dangerouslySetInnerHTML={{ __html: template.conteudo }}
                                />
                            )}
                            {viewMode === 'source' && (
                                <textarea 
                                    name="conteudo" 
                                    value={template.conteudo} 
                                    onChange={onChange} 
                                    rows="15" 
                                    className="editor-source-textarea"
                                    placeholder="Conteúdo HTML do template..."
                                />
                            )}
                        </div>
                    </fieldset>

                    <fieldset className="form-section">
                        <legend className="form-section-title">Configurações</legend>
                        <div className="form-group-checkbox">
                            <input 
                                type="checkbox" 
                                name="ativo" 
                                id="contrato-ativo" 
                                checked={template.ativo} 
                                onChange={onChange} 
                                className="form-checkbox" 
                            />
                            <label htmlFor="contrato-ativo">Template ativo</label>
                        </div>
                        {isMatrizAdmin && (
                            <div className="form-group-checkbox mt-4">
                                <input 
                                    type="checkbox" 
                                    name="isPadrao" 
                                    id="contrato-isPadrao" 
                                    checked={template.isPadrao} 
                                    onChange={onChange} 
                                    className="form-checkbox" 
                                />
                                <label htmlFor="contrato-isPadrao">Template Padrão Global</label>
                            </div>
                        )}
                    </fieldset>
                    
                    <div className="form-actions">
                        <button 
                            type="button" 
                            onClick={handleClose} 
                            className="action-button action-button-secondary"
                        >
                            <XCircle size={18} className="icon"/> Cancelar
                        </button>
                        <button 
                            type="submit" 
                            disabled={overallLoading} 
                            className="action-button action-button-primary"
                        >
                            {overallLoading ? (
                                <Loader2 className="animate-spin icon"/>
                            ) : (
                                <Save size={18} className="icon"/>
                            )}
                            {current ? 'Atualizar Template' : 'Salvar Template'}
                        </button>
                    </div>
        </form>
    );
};

export default TemplateContratoForm;